import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  alpha,
  useTheme,
  Chip,
  Button,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

// Icons
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";

// Components
import AppModal from "../common/AppModal";
import FormAlert from "../common/FormAlert";
import FormSelectField from "../common/FormSelectField";
import FormTextField from "../common/FormTextField";
import SubmitButton from "../buttons/SubmitButton";
import AllocatePaymentModal from "./AllocatePaymentModal";

// Redux
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getBranchSettings } from "../../redux/actions/settingsActions";
import { usePermissions } from "../../hooks/usePermissions";

// Types & Utils
import { PaymentRequestDto } from "../../shared/dtos/payment/PaymentRequestDto";
import { PaymentMethod } from "../../shared/enums/PaymentMethod";
import { formatCurrency, formatPlainNumber } from "../../utils/appUtils";
import { castEnumForDisplay } from "../../helpers/enumHelpers";

export type PaymentEntityType = "Purchase Order";

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityType: PaymentEntityType;
  documentNumber: string;
  balance: number;
  currencyCode?: string;
  loading: boolean;
  error: string | null;
  onSubmitPayment: (entityId: string, payload: PaymentRequestDto) => void;
}

interface PaymentFormValues {
  paymentMethod: PaymentMethod;
  amount: number | "";
  referenceCode: string;
  narrative: string;
  unallocatedPaymentId: string | null;
  unallocatedMaxAmount: number | null;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  open,
  onClose,
  entityId,
  entityType,
  documentNumber,
  balance,
  currencyCode = "KES",
  loading,
  error,
  onSubmitPayment,
}): React.JSX.Element => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const { branchSettings } = useAppSelector((state) => state.settings);

  const [allocateModalOpen, setAllocateModalOpen] = useState<boolean>(false);

  const canAllocate = hasPermission("unallocated_payment:read_all");

  useEffect(() => {
    if (!branchSettings && canAllocate) {
      dispatch(getBranchSettings());
    }
  }, [branchSettings, canAllocate, dispatch]);

  // Direct enum options mapping
  const paymentMethodOptions = useMemo(
    () =>
      Object.values(PaymentMethod).map((method) => ({
        value: method,
        label: castEnumForDisplay(method) || method.replace(/_/g, " "),
      })),
    [],
  );

  const paymentSchema = Yup.object().shape({
    paymentMethod: Yup.mixed<PaymentMethod>()
      .oneOf(Object.values(PaymentMethod), "Payment method is required")
      .required("Payment method is required"),
    amount: Yup.number()
      .transform((value, originalValue) =>
        typeof originalValue === "string" ? Number(originalValue) : value,
      )
      .required("Amount is required")
      .positive("Must be greater than 0")
      .test("max-allowed", "Amount exceeds limits", function (value) {
        const { unallocatedMaxAmount } = this.parent;
        const maxAllowed = unallocatedMaxAmount
          ? Math.min(balance, unallocatedMaxAmount)
          : balance;

        if (value && value > maxAllowed + 0.01) {
          return this.createError({
            message: `Cannot exceed ${formatPlainNumber(maxAllowed)} (Balance/Funds limit)`,
          });
        }
        return true;
      }),
    referenceCode: Yup.string().trim().nullable(),
    narrative: Yup.string().trim().nullable(),
  });

  const initialValues: PaymentFormValues = {
    paymentMethod: PaymentMethod.CASH,
    amount: balance > 0 ? balance : "",
    referenceCode: "",
    narrative: "",
    unallocatedPaymentId: null,
    unallocatedMaxAmount: null,
  };

  const isPayableByUs = entityType === "Purchase Order";
  const title = isPayableByUs
    ? `Pay Supplier (${entityType} #${documentNumber})`
    : `Receive Payment (${entityType} #${documentNumber})`;
  const balanceLabel = isPayableByUs
    ? "Outstanding Balance to Pay"
    : "Total Outstanding Balance";

  return (
    <AppModal
      open={open}
      onClose={loading ? () => {} : onClose}
      title={title}
      width="sm"
    >
      <FormAlert message={error} severity="error" />
      <Formik
        initialValues={initialValues}
        validationSchema={paymentSchema}
        validateOnMount
        onSubmit={(values) => {
          const payload: PaymentRequestDto = {
            paymentMethod: values.paymentMethod,
            amount: Number(values.amount),
            referenceCode: values.referenceCode
              ? values.referenceCode.trim()
              : null,
            narrative: values.narrative ? values.narrative.trim() : null,
          };
          onSubmitPayment(entityId, payload);
        }}
      >
        {({
          values,
          setValues,
          isValid,
          dirty,
        }: FormikProps<PaymentFormValues>) => (
          <Form>
            <Stack spacing={2.5}>
              {/* BALANCE SUMMARY */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {balanceLabel}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "secondary.main", fontWeight: "bold" }}
                  >
                    {formatCurrency(balance, currencyCode)}
                  </Typography>
                </Stack>
              </Box>

              {/* ELECTRONIC ALLOCATION CARD */}
              {canAllocate && branchSettings?.paymentIntegration?.active && (
                <Box
                  sx={{
                    p: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: "bold" }}
                  >
                    ALLOCATE EXISTING TRANSACTION
                  </Typography>

                  {values.unallocatedPaymentId ? (
                    <Box>
                      <Chip
                        color="success"
                        icon={<CheckCircleIcon />}
                        label={`Linked: ${values.referenceCode} (Available: ${values.unallocatedMaxAmount})`}
                        onDelete={() => {
                          setValues(
                            {
                              ...values,
                              unallocatedPaymentId: null,
                              referenceCode: "",
                              unallocatedMaxAmount: null,
                              amount: balance,
                            },
                            true,
                          );
                        }}
                        sx={{ fontWeight: "bold", mb: 1 }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: "block" }}
                      >
                        * Linked transaction applied. You can edit the payment
                        amount below if needed.
                      </Typography>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<LinkIcon />}
                      onClick={() => setAllocateModalOpen(true)}
                    >
                      Link Unallocated Payment
                    </Button>
                  )}
                </Box>
              )}

              {/* STANDARD FORM FIELDS */}
              <FormSelectField
                name="paymentMethod"
                label="Payment Method"
                options={paymentMethodOptions}
              />

              <FormTextField name="amount" label="Amount" type="number" />

              <FormTextField
                name="referenceCode"
                label="Reference Code / Cheque / Bank Trans ID"
                slotProps={{
                  input: {
                    readOnly: !!values.unallocatedPaymentId,
                  },
                }}
                sx={{
                  "& .MuiInputBase-input.Mui-readOnly": {
                    backgroundColor: alpha(
                      theme.palette.action.disabledBackground,
                      0.1,
                    ),
                    cursor: "not-allowed",
                  },
                }}
              />

              <FormTextField
                name="narrative"
                label="Narrative / Description (Optional)"
                placeholder="Optional memo notes"
              />

              <SubmitButton
                title={
                  isPayableByUs
                    ? "Record Outbound Payment"
                    : "Record Received Payment"
                }
                loadingText="Processing..."
                loading={loading}
                disabled={
                  !isValid ||
                  (!dirty && !values.unallocatedPaymentId) ||
                  loading
                }
                startIcon={<PaymentIcon />}
                sx={{ mt: 1, py: 1.5 }}
              />
            </Stack>

            {/* UNALLOCATED PAYMENT MODAL */}
            {allocateModalOpen && (
              <AllocatePaymentModal
                open={allocateModalOpen}
                onClose={() => setAllocateModalOpen(false)}
                onSelect={(payment) => {
                  const paymentAmount = Number(payment.amount);
                  const allocatableAmount = Math.min(paymentAmount, balance);

                  setValues(
                    {
                      ...values,
                      unallocatedPaymentId: payment.id,
                      referenceCode: payment.externalReference || "",
                      unallocatedMaxAmount: paymentAmount,
                      amount: Number(allocatableAmount.toFixed(2)),
                      paymentMethod: PaymentMethod.MPESA,
                    },
                    true,
                  );

                  setAllocateModalOpen(false);
                }}
              />
            )}
          </Form>
        )}
      </Formik>
    </AppModal>
  );
};

export default RecordPaymentModal;
